import React, { useCallback, useState } from 'react'
import { useDealership } from '../../../../../store/zustand/dealershipStore'
import { Outlet, useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'
import { ReactComponent as IconImport } from '../../../../../images/icons/icon_import_large.svg'
import { ReactComponent as IconCreate } from '../../../../../images/icons/icon_create_large.svg'
import { ReactComponent as IconCustomers } from '../../../../../images/icons/icon_customer_large.svg'
import { ReactComponent as InventoryIcon } from '../../../../../images/icons/inventory_campaign.svg'
import { ReactComponent as ArrowIcon } from '../../../../../images/list_arrow.svg'
import { useGetLinkedAccounts } from '../../../../../api/agencies/useGetLinkedAccounts'
import { VENDOR } from '../../../../../lib/utils/vendors'
import { Link } from 'react-router-dom'
import { Button } from '@demandlocal/components'
import { motion } from 'framer-motion'
import { User } from '../../../../../lib/types/User'
import { useSelector } from 'react-redux'
import { currentUserSelector } from '../../../../../store/selectors/userSelector'

export default function SetupProfile(): JSX.Element {
  const [optionSelected, setOptionSelected] = useState(0)
  const dealership = useDealership()
  const navigate = useNavigate()
  const user: User | null = useSelector(currentUserSelector)
  const { data, isLoading: isLoadingLinkedAccounts } = useGetLinkedAccounts()
  const oauthConnected =
    !isLoadingLinkedAccounts &&
    data?.find(
      (account) =>
        [VENDOR.Google, VENDOR.Facebook].includes(account.id) && account.connected,
    )
  const { t } = useTranslation('routes')

  const onSubmit = useCallback(() => {
    switch (optionSelected) {
      case 1: {
        navigate(t('dealership.customers.sources.create', { dealership }))
        break
      }
      case 2: {
        navigate(t('dealership.vehicles.overview', { dealership }))
        break
      }
      case 3: {
        navigate(`${t('dealership.campaigns.create', { dealership })}/new`)
        break
      }
      case 4: {
        navigate(`${t('dealership.campaigns.create', { dealership })}/import`)
        break
      }
      default: {
        return
      }
    }
  }, [dealership, navigate, optionSelected, t])

  return (
    <div className="flex flex-col w-full px-7 my-auto items-center min-h-[700px] relative">
      <div className="flex flex-col items-center w-full">
        <span className="text-2xl font-bold">Welcome to LinkOne. Let's get started!</span>
        <span className="text-base font-Spartan pt-1 pb-8">
          Choose any of the following to continue your setup process
        </span>
        <div className="grid grid-cols-4 gap-4 px-6">
          <div
            className={classNames(
              'flex flex-1 shrink flex-col py-8 px-3 gap-4 rounded-xl border-[3px] cursor-pointer transition ease-in-out duration-500',
              {
                'border-primary-persian-blue bg-primary-persian-blue/10':
                  optionSelected === 1,
                'border-web-gray/20': optionSelected !== 1,
              },
            )}
            onClick={(e) => {
              e.preventDefault()
              setOptionSelected(1)
            }}>
            <div className="flex flex-row justify-center items-center">
              <IconCustomers />
            </div>
            <span className="font-bold text-center">Import your Customers</span>
            <ul className="px-2 space-y-2">
              <li className="flex items-start gap-2">
                <ArrowIcon className="h-6 w-6 mt-0.5 flex-shrink-0 " />
                <span>
                  Import your first party data from one of our many integrations
                </span>
              </li>
              <li className="flex items-start gap-2">
                <ArrowIcon className="h-6 w-6 mt-0.5 flex-shrink-0 " />
                <span>View and monitor your CRM/DMS data</span>
              </li>
            </ul>
          </div>
          <div
            className={classNames(
              'flex flex-1 shrink flex-col py-8 px-3 gap-4 rounded-xl border-[3px] cursor-pointer transition ease-in-out duration-500',
              {
                'border-primary-persian-blue bg-primary-persian-blue/10':
                  optionSelected === 2,
                'border-web-gray/20': optionSelected !== 2,
              },
            )}
            onClick={(e) => {
              e.preventDefault()
              setOptionSelected(2)
            }}>
            <div className="flex flex-row justify-center items-center">
              <InventoryIcon />
            </div>
            <span className="font-bold text-center">Import your Inventory</span>
            <ul className="px-2 space-y-2">
              <li className="flex items-start gap-2">
                <ArrowIcon className="h-6 w-6 mt-0.5 flex-shrink-0 " />
                <span>Import your dealer's inventory from your site or via FTP</span>
              </li>
              <li className="flex items-start gap-2">
                <ArrowIcon className="h-6 w-6 mt-0.5 flex-shrink-0 " />
                <span>Create a new marketing campaign using your inventory</span>
              </li>
            </ul>
          </div>
          <div
            className={classNames(
              'flex flex-1 shrink flex-col py-8 px-3 gap-4 rounded-xl border-[3px] cursor-pointer transition ease-in-out duration-500',
              {
                'border-primary-persian-blue bg-primary-persian-blue/10':
                  optionSelected === 3,
                'border-web-gray/20': optionSelected !== 3,
              },
            )}
            onClick={(e) => {
              e.preventDefault()
              setOptionSelected(3)
            }}>
            <div className="flex flex-row justify-center items-center">
              <IconCreate />
            </div>
            <span className="font-bold text-center">
              Create a marketing campaign with our help
            </span>
            <ul className="px-2 space-y-2">
              <li className="flex items-start gap-2">
                <ArrowIcon className="h-6 w-6 mt-0.5 flex-shrink-0 " />
                <span>Own your market, SEM, Video, display</span>
              </li>
              <li className="flex items-start gap-2">
                <ArrowIcon className="h-6 w-6 mt-0.5 flex-shrink-0 " />
                <span>Conquest your competitors and target Audiences likely to buy</span>
              </li>
            </ul>
          </div>
          <div
            className={classNames(
              'flex flex-1 shrink flex-col py-8 px-3 gap-4 rounded-xl border-[3px] transition ease-in-out duration-500',
              {
                'border-primary-persian-blue bg-primary-persian-blue/10':
                  optionSelected === 4,
                'border-web-gray/20': optionSelected !== 4,
                'opacity-50 cursor-select': !oauthConnected,
                'cursor-pointer': oauthConnected,
              },
            )}
            onClick={(e) => {
              e.preventDefault()
              if (oauthConnected) setOptionSelected(4)
            }}>
            <div className="flex flex-row justify-center items-center">
              <IconImport />
            </div>
            <span className="font-bold text-center">
              Import your own self service campaigns
            </span>
            <ul className="px-2 space-y-2">
              <li className="flex items-start gap-2">
                <ArrowIcon className="h-6 w-6 mt-0.5 flex-shrink-0 " />
                <span>Import your marketing campaigns from Google Ads & Meta</span>
              </li>
              <li className="flex items-start gap-2">
                <ArrowIcon className="h-6 w-6 mt-0.5 flex-shrink-0 " />
                <span>View your marketing performance and results</span>
              </li>
            </ul>
            {!oauthConnected &&
              (Number(user?.role_id ?? 3) > 2 ? (
                <span className="font-bold text-flame text-sm text-center">
                  To import campaigns, contact your agency administrator to OAuth their
                  account via Agency {'>'} Linked Platforms
                </span>
              ) : (
                <span className="font-bold text-flame text-sm text-center">
                  To import campaigns, you must first OAuth your account via{' '}
                  <Link
                    className="text-primary-persian-blue"
                    to={t('agency.profile.accounts', {
                      agency_id: dealership?.organization_id,
                    })}>
                    this link.
                  </Link>
                </span>
              ))}
          </div>
        </div>
      </div>

      {optionSelected > 0 && (
        <motion.div
          className="pt-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5 }}>
          <Button
            onClick={(e) => {
              e.preventDefault()
              onSubmit()
            }}>
            Continue
          </Button>
        </motion.div>
      )}

      <Outlet />
    </div>
  )
}
